import { LessonChatMode, THistoryMessageOpenText } from '@/types/firebase'
import { questionToSpeakableTextPrompt, solutionRequestText } from '../lesson'
import {
  IGptConversationMessage,
  IGptResponseQuestionToSpeakableText,
  MessageRole,
} from '@/types/gpt'
import questionToSpeakableTextGptRequest from '@/app/api/gpt-question-to-speakable-text'
import { IGenericError } from '@/types/shared'
import { ResponseFormatJSONSchema } from 'openai/resources/shared'
import {
  SCHEME_CHECK_REQUIRED,
  schemeCheckInstruction,
  schemeCheckProperties,
} from './scheme-check'

export async function getQuestionSpeakableText(
  variantId: string,
  variantQuestion: string,
): Promise<IGptResponseQuestionToSpeakableText> {
  try {
    // generate prompt for gpt
    const prompt = questionToSpeakableTextPrompt(variantQuestion)

    // generate system message
    const promptForGptMessage: THistoryMessageOpenText = {
      content: prompt,
      role: MessageRole.System,
      currentVariantId: variantId,
    }

    const gptResponse: IGptResponseQuestionToSpeakableText | null | undefined =
      await questionToSpeakableTextGptRequest([promptForGptMessage])

    if (!gptResponse) {
      throw new Error(`Error on questionToSpeakableTextGptRequest`)
    }

    return gptResponse
  } catch (error: unknown) {
    console.error(
      `error while trying to get question speakableText for variant, id: ${variantId} :>>`,
      (error as IGenericError)?.message || error,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get question speakableText for variant`,
    )
  }
}

export function generateModelSolutionFunctionSchema(): Record<string, any> {
  return {
    type: 'object',
    name: 'provide_model_solution',
    description: 'Generate exam-style model solution based on mark scheme',
    parameters: {
      type: 'object',
      properties: {
        gptResponseToStudent: {
          type: 'string',
          description:
            'Complete model solution with proper formatting that addresses all mark scheme points',
        },
      },
      required: ['gptResponseToStudent'],
    },
  }
}

export function generateModelSolutionConversation({
  question,
  markScheme,
}: {
  question: string
  markScheme: string
}): IGptConversationMessage[] {
  // Create a specialized prompt for mark scheme-based solutions
  const modelSolutionPrompt = `Generate a clear, step-by-step model solution for the following exam question. Use the provided mark scheme to ensure your solution covers all required points that would earn full marks.

Question: ${question}

Mark Scheme: ${markScheme}

Follow these formatting guidelines:
1. Mathematical Expression Formatting:
		- Inline math: Use $...$
			Example: "The value of $x^2$ is..."
		- Block math: Use $$...$$ on **ONE LOGICAL LINE ONLY**.
		- Block equations MUST NOT contain real line breaks (\\n) or surrounding whitespace inside the $$...$$ delimiters.
		- **RULE FOR MULTI-LINE STRUCTURES (e.g., arrays):** Complex or multi-line LaTeX environments (e.g., \\\\begin{array})**MUST BE** entirely enclosed within $$...$$ and remain on a single logical line, with no real line breaks (\\n) or surrounding whitespace inside the $$...$$ delimiters.
			Example (CORRECT): "The value of $$x^2$$ is..."
		- **All** LaTeX commands must use **DOUBLE** backslashes.
			Example (CORRECT): "$$ \\\\begin{array}{r} 37.80\\\\ + 8.97\\\\ \\\\hline \\\\end{array} $$"
		- Do NOT use \\( \\), \\[ \\], or any other math delimiters.
		- Use ONLY $...$ for inline math and $$...$$ for block math.
		- Always use double backslashes ("\\\\") instead of single backslashes: \\\\frac, \\\\sqrt, \\\\cdot .
			Example: "$u = 10\\\\frac{m}{s}$" (correct).
		- No single backslashes, no unescaped LaTeX.
		- Avoid: "$u = 10\\frac{m}{s}$" or "$u = 10\ \frac{m}{s}$" (incorrect)
		- Review all KaTeX expressions for proper formatting.
		- If studentAnswerIncludesKaTexSyntax is "true", verify KaTeX syntax in your response.

2. Currency Notation Rule:
	- For all monetary amounts, prices, or financial calculations, ALWAYS use the text-based currency code (USD, EUR, GBP) or the full currency name (e.g., "dollars") instead of their symbols.

3. Text Structure:
		- Start each step with "Step N:" followed by a brief description
		- End with "Final Answer:" followed by the complete solution

4. Step Format:
		Step N: [Description]
		[Explanation]
		[Mathematical working]
		[Conclusion for this step]

5. Structure your answer in clear steps:
		- Label each step (e.g., "Step 1: Understand the problem")
		- Include explanations for your reasoning
		- Highlight key points from the mark scheme
		- Conclude with a final answer that addresses all parts of the question

Please provide a complete model solution that would earn full marks according to the provided marking scheme.`

  return [{ role: MessageRole.User, content: modelSolutionPrompt }]
}

export function generateLessonWithMarksAvailableFunctionSchema({
  shouldProcessMarks,
  totalMarks = 0,
  currentQuestion = '',
  givenHintCount = 0,
  attemptCount = 0,
}: {
  shouldProcessMarks: boolean
  totalMarks: number
  currentQuestion: string
  givenHintCount: number
  attemptCount: number
}): Record<string, any> {
  return {
    type: 'object',
    name: 'grade_task',
    description: `As an AI tutor, provide encouraging and constructive feedback that builds on the student's previous work and understanding. Evaluate answers through a growth mindset lens, focusing on progress and learning opportunities. Return a JSON response with: gptResponseToStudent: A warm, personalized message that acknowledges effort and guides learning. isCompleted: Boolean indicating if learning objectives are met for the last question. For incorrect answers: Guide with scaffolded support, building on existing understanding and previous attempts. Never reveal solutions before meaningful engagement. Assume mathematical competence unless steps are explicitly requested. Grade as Correct, Incorrect, or Partially Correct while maintaining encouraging, growth-focused feedback.${
      shouldProcessMarks
        ? ` For interactive papers, assess the student's answer against the mark scheme criteria and award marks out of the total available ${totalMarks} marks for this question. Use the mark scheme to determine exactly how many marks were earned for each point addressed in the answer.`
        : ''
    }`,
    parameters: {
      type: 'object',
      properties: {
        gptResponseToStudent: {
          type: 'string',
          description:
            'Craft personalized feedback that acknowledges effort and guides learning. For mathematical expressions, use SIMPLE LaTeX formatting with single backslashes and prefer inline math ($x = 5$) over display math when possible. For complex formulas, use clear single-line expressions like $\\frac{a}{b}$ rather than multi-line displays. This ensures better readability during streaming. Build on previous interactions and understanding. For unrelated answers, gently redirect to core concepts. Avoid direct quotes of student work, instead reformulating with correct notation.' +
            (shouldProcessMarks
              ? ' For interactive papers, add detailed breakdown of the awarded marks.'
              : ''),
        },
        points: {
          type: 'string',
          description: `Award points on a 5-point scale (X/5) based on demonstrated understanding and progress of the current question only: ${currentQuestion}. Consider partial credit for meaningful attempts and logical reasoning of the current question. Award full points (5/5) for complete, accurate solutions of the current attempt. If the response is unrelated or seeks guidance without attempt or is referring to previous conversation, withhold points while maintaining encouragement.`,
        },
        actualMarks: shouldProcessMarks
          ? {
              type: 'number',
              description: `For interactive papers only: The actual number of marks awarded to the student based on their current answer. This must be an integer between 0 and ${totalMarks}. DO NOT use decimal or fractional marks - round to the nearest whole number. Carefully evaluate the answer against the mark scheme criteria, using standard exam notation for method marks (M1, M2) and accuracy marks (A1, A2). Award method marks (M) for correct approaches and working shown, even if the final answer is incorrect. Award accuracy marks (A) for correct numerical results or conclusions. For each mark awarded, document exactly what the student did to earn that mark. The total marks should be the sum of all M and A marks. The detailed explanation for each awarded mark must be provided in the 'marksBreakdown' field.`,
            }
          : undefined,
        marksBreakdown: shouldProcessMarks
          ? {
              type: 'string',
              description:
                'A bulleted list of each specific mark awarded based on the mark scheme. Separate each mark with descriptions using the format "M1: [description of method mark]" or "A1: [description of accuracy mark]". Start each mark description from a new line. This breakdown helps the student understand exactly which parts of their answer earned marks. This field should contain only the list of marks, without any introductory text like "Marks awarded:". **If actualMarks is 0, return an empty string ("").**',
            }
          : undefined,
        totalMarks: shouldProcessMarks
          ? {
              type: 'number',
              description: `For interactive papers only: The total number of marks available for this question is ${totalMarks}. Return this exact value as a number, not a string.`,
            }
          : undefined,
        lineAnnotations: shouldProcessMarks
          ? {
              type: 'array',
              description:
                'For interactive papers only: walk the student\'s working line by line and tag EACH step with how it fared against the mark scheme. One entry per distinct step or line of the student answer (quote it briefly). status: "earned" when the step earned its mark-scheme code (green in the UI), "slipped" when this step is where a mark was lost (amber in the UI), "info" for neutral context. markCode: the code from the mark scheme this step maps to (e.g. M1, A1) or "none". note: one short sentence - what the mark was earned for, or exactly what went wrong and what the correct step was. Keep every note under 20 words.',
              items: {
                type: 'object',
                properties: {
                  line: {
                    type: 'string',
                    description:
                      "The student's step, quoted briefly (LaTeX allowed).",
                  },
                  markCode: {
                    type: 'string',
                    description:
                      'Mark scheme code this step maps to (M1, A1, ...) or "none".',
                  },
                  status: {
                    type: 'string',
                    enum: ['earned', 'slipped', 'info'],
                    description:
                      'earned = mark gained here, slipped = mark lost here, info = neutral.',
                  },
                  note: {
                    type: 'string',
                    description:
                      'One short sentence: why earned, or what went wrong + the fix.',
                  },
                },
                required: ['line', 'markCode', 'status', 'note'],
              },
            }
          : undefined,
        isCompleted: {
          type: 'boolean',
          description: `Set 'true' when learning objectives are met through: meaningful engagement (${givenHintCount} ≥ 3), persistent effort (${attemptCount} ≥ 3), or demonstrated mastery (points = 5/5). Otherwise 'false'. Withhold final solution until completion criteria met of the last question. If he has not given the answer set this as false`,
        },
        isAnswerRelevant: {
          type: 'boolean',
          description:
            "Assess alignment with core question objectives. Set 'true' for responses that engage with the fundamental concepts, even if imperfectly. Consider previous context and build on prior understanding.",
        },
        answerRelevantDescription: {
          type: 'string',
          description:
            "Provide detailed analysis of relevance, comparing student's approach with solution path. Include specific examples from student work and solution for transparent evaluation. Focus on understanding demonstrated rather than just correctness.",
        },
        isAnswered: {
          type: 'boolean',
          description: `Set this to 'true' ONLY if the student's answer is mathematically equivalent to the correct solution. Consider different forms (e.g. factored vs expanded) as correct if they are algebraically equivalent. For numerical answers, allow reasonable rounding differences. For proofs, require logical equivalence. For multi-part questions, such as parts (a) and (b), the student must correctly respond to all required parts to receive a 'true'. Answers phrased with uncertainty (a trailing question mark, "maybe", "I think", "is it ...?") still earn 'true' when the stated answer itself is correct - never withhold 'true' for hesitant phrasing; instead, briefly note the uncertainty in your feedback as a confidence nudge. Also, set 'true' only if answer is correct AND either: a) Question doesn't require working steps, OR b) Question contains "show your working"/"show steps" AND working is provided`,
        },
        studentAnswerIncludesKaTexSyntax: {
          type: 'boolean',
          description:
            "Identify if last students answer includes KaTeX syntax. Set 'true' if mathematical notation present, 'false' otherwise. Ensures proper handling of mathematical expressions in responses.",
        },
        isPartiallyCorrect: {
          type: 'boolean',
          description: `Recognize partial understanding and progress. Consider both explicit solutions and implicit understanding. Acknowledge valid approaches even if final answer isn't perfect. The current question is ${currentQuestion}`,
        },
        incrementGivenHintCount: {
          type: 'boolean',
          description:
            "Track meaningful guidance provided. Set 'true' when offering substantive hints or addressing core concept questions. 'False' for general clarifications or non-hint responses.",
        },
        incrementAttemptCount: {
          type: 'boolean',
          description:
            "Track substantive solution attempts. Set 'true' for direct answer attempts, 'false' for clarification requests, hint seeking, or tangential responses.",
        },
        gptResponseToStudentText: {
          type: 'string',
          description:
            "Convert mathematical feedback into natural, spoken language. Transform KaTeX notation into clear verbal descriptions (e.g., '$\\\\frac{1}{2}$' becomes 'one half'). Maintain supportive tone while ensuring mathematical clarity.",
        },
        isHintRequest: {
          type: 'boolean',
          description: `Set 'true' if the student's question requests problem-solving help, hints, or guidance specific to answering the question. Set 'false' for general course/procedure questions, clarification requests, or non-content inquiries.`,
        },
        isLastQuestionCompleted: {
          type: 'boolean',
          description: `Keep 'true' if the last question in conversation was completed, and new one or similar not provided yet. This also includes cases where: - The tutor confirms that the student's answer for the last question is correct. - The student answered the last question correctly. - The tutor reveals the correct answer, the correct answer with solution, or the correct answer with reasoning for the last question. Set 'false' immediately when a new question is provided. This includes cases where: - The correct answer was not provided by student for the last question in conversation. - The student requests for a new question or similar question. - You generate a new or similar question for the student. - You generate a new follow-up question for the student.`,
        },
        isSolutionResponse: {
          type: 'boolean',
          description: `Set to true if this response reveals step by step solution requested with "Request for solution pattern".`,
        },
        tip: {
          type: 'string',
          description: `Provide a helpful strategy or shortcut for similar problems. Use when: 1) There's a reusable problem-solving approach, 2) A particular technique simplifies solutions, 3) It helps generalize the learning. Format: "For similar problems, try [concrete action] because [brief rationale]." Only include when directly relevant to the solution and isSolutionResponse is set to "true".`,
        },
        commonMistake: {
          type: 'string',
          description: `Describe typical errors students make with this problem. Use when: 1) There's a common misconception, 2) The error pattern recurs frequently, 3) It helps prevent future mistakes. Format: "Students often [specific error] because [reason]. Remember that [correction]." Include only for solution responses when isSolutionResponse is set to "true".`,
        },
      },
      required: [
        'gptResponseToStudent',
        'isAnswered',
        'isCompleted',
        'isPartiallyCorrect',
        'incrementGivenHintCount',
        'points',
        'incrementAttemptCount',
        'isAnswerRelevant',
        'answerRelevantDescription',
        'gptResponseToStudentText',
        'studentAnswerIncludesKaTexSyntax',
        'isHintRequest',
        'isSolutionResponse',
        'isLastQuestionCompleted',
        ...(shouldProcessMarks
          ? ['actualMarks', 'totalMarks', 'lineAnnotations']
          : []),
      ],
    },
  }
}

export function generateGeneralFeedbackBasedOnMarkSchemeFunctionSchema(): Record<
  string,
  any
> {
  return {
    type: 'object',
    name: 'provide_exam_feedback',
    description:
      'Generate examiner-style feedback based on mark scheme criteria',
    parameters: {
      type: 'object',
      properties: {
        gptResponseToStudent: {
          type: 'string',
          description:
            'Detailed, constructive feedback that directly addresses the student (using "you" not "the student") and helps them improve based on mark scheme requirements',
        },
      },
      required: ['gptResponseToStudent'],
    },
  }
}

export function generateGeneralFeedbackBasedOnMarkSchemeConversation({
  question,
  studentAnswer,
  markScheme,
}: {
  question: string
  studentAnswer: string
  markScheme: string
}): IGptConversationMessage[] {
  // Create a specialized prompt for mark scheme-based feedback
  const generalFeedbackPrompt = `
As an experienced examiner, provide constructive feedback directly to the student (using "you" instead of "the student") based on the mark scheme.

Question: ${question}

Student's Answer: ${studentAnswer}

Mark Scheme: ${markScheme}

Follow these formatting guidelines:
1. Mathematical Expression Formatting:
	- Inline math: Use $...$
		Example: "The value of $x^2$ is..."
	- Block math: Use $$...$$ on **ONE LOGICAL LINE ONLY**.
		Example (CORRECT): "The value of $$x^2$$ is..."
	- Block equations MUST NOT contain real line breaks (\\n) or surrounding whitespace inside the $$...$$ delimiters.
	- **RULE FOR MULTI-LINE STRUCTURES (e.g., arrays):** Complex or multi-line LaTeX environments (e.g., \\\\begin{array})**MUST BE** entirely enclosed within $$...$$ and remain on a single logical line, with no real line breaks (\\n) or surrounding whitespace inside the $$...$$ delimiters.
		Example (CORRECT): "$$ \\\\begin{array}{r} 37.80\\\\ + 8.97\\\\ \\\\hline \\\\end{array} $$"
	- **All** LaTeX commands must use **DOUBLE** backslashes.
		Example (CORRECT): "$u = 10\\\\frac{m}{s}$"
	- Do NOT use \\( \\), \\[ \\], or any other math delimiters.
	- Use ONLY $...$ for inline math and $$...$$ for block math.
	- Always use double backslashes ("\\\\") instead of single backslashes: \\\\frac, \\\\sqrt, \\\\cdot .
		Example: "$u = 10\\\\frac{m}{s}$" (correct).
	- No single backslashes, no unescaped LaTeX.
	- Avoid: "$u = 10\\frac{m}{s}$" or "$u = 10\ \frac{m}{s}$" (incorrect)
	- Review all KaTeX expressions for proper formatting.
	- If studentAnswerIncludesKaTexSyntax is "true", verify KaTeX syntax in your response.

2. Currency Notation Rule:
	- For all monetary amounts, prices, or financial calculations, ALWAYS use the text-based currency code (USD, EUR, GBP) or the full currency name (e.g., "dollars") instead of their symbols.

3. Text Structure:
	- Organize feedback into distinct sections with headings
	- Ensure adequate spacing for readability

4. Content Guidelines:
	- Address the student directly using "you" and "your" (not "the student")
	- Start with "Strengths:" section highlighting what was done well
	- Follow with "Areas for Improvement:" section
	- End with "Examiner's Summary:" giving overall assessment

5. Structure your feedback in clear sections:
	- Start with a brief overview of their performance
	- Identify specific strengths in their response
	- Highlight key areas for improvement to achieve maximum marks
	- Suggest specific ways they can enhance their answer
	- Explain which mark scheme points they addressed well and which they missed

Your feedback should be constructive, specific, and directly tied to the mark scheme requirements. Focus on how the student can improve their answer to achieve the maximum possible marks in an exam setting.

IMPORTANT: Always write as if you are speaking directly to the student. Avoid phrases like "the student should" or "the student's response" - instead use "you should" and "your response."`

  return [{ role: MessageRole.User, content: generalFeedbackPrompt }]
}

export function generateAiAssistantFunctionSchema({
  currentTopic,
  isRecommendationConversation,
  useLoadingTextParts = true,
}: {
  currentTopic?: string
  isRecommendationConversation?: boolean
  useLoadingTextParts?: boolean
}): Record<string, any> {
  return {
    type: 'object',
    name: 'ai_assistant_response',
    description: `You are a helpful AI assistant for a math tutoring app called Visely. Your role is to provide educational support to students by answering their questions clearly and helpfully. Always be encouraging and supportive. When discussing mathematical concepts, use proper mathematical notation and provide step-by-step explanations when appropriate. If the current conversation topic is provided: "${currentTopic}", try to relate your response to this topic when relevant.`,
    parameters: {
      type: 'object',
      properties: {
        text: {
          type: 'string',
          description:
            'The main response content, providing an answer or explanation. This field is mandatory. This field should not contain any questions or prompts. This field should maintain the current context unless the student explicitly confirms switching topics.',
        },
        topic: {
          type: 'string',
          description: `
										The general topic of discussion used as title for conversation, such as "Algebra" or "Calculus". 
											- This field is not mandatory and can be used as a title for a specific theme.
											- Always start this field with a capital letter.
											- If the topic shifts naturally within the conversation, update it accordingly while ensuring continuity.
											- ${
                        currentTopic
                          ? `The current topic is "${currentTopic}". If the discussion context changes, update this field to reflect the new focus accurately.`
                          : ''
                      }`,
        },
        visualRepresentation: {
          type: 'string',
          description: `An optional LaTeX TikZ diagram that visually represents the concept or problem being explained.This should be included only when:
											- The user specifically asks for a diagram or visual.
											- A visual aid would significantly improve clarity or understanding.
										The diagram is rendered by TikZJax, a legacy TikZ engine with NO extra TikZ libraries available, so these rules are hard requirements: use ONLY classic arrow tips - write "->" or ">=stealth", NEVER ">=Latex", ">=Stealth" or any arrows.meta tip name; do NOT use pgfplots (no \\begin{axis} - plot curves with \\draw plot instead); do NOT use the positioning library "of" syntax like "below=4pt of P" - place nodes with explicit coordinates or "below of=P"; no \\usepackage and no \\usetikzlibrary; remember pgf math trig functions work in DEGREES (sin(90)=1) - for radian-valued curves write sin(\\x r) and keep every coordinate within about -20..20 so dimensions do not overflow. Output raw TikZ only: no markdown code fences, no captions or labels like "Diagram:" - just \\begin{tikzpicture}...\\end{tikzpicture}.`,
        },
        example: {
          type: 'string',
          description:
            'An optional example problem and its solution to illustrate the concept. If provided, this will unlock additional fields such as tip and commonMistake.For better readability, especially with long expressions, break them into smaller parts to prevent overflow.',
        },
        tip: {
          type: 'string',
          description:
            'An optional helpful strategy or technique for understanding or solving related problems. Shown only if "example" is provided.',
        },
        commonMistake: {
          type: 'string',
          description:
            'An optional common error students make and how to avoid it. Shown only if "example" is provided.',
        },
        followUpQuestion: {
          type: 'string',
          description:
            'An additional question to reinforce learning and encourage engagement. This field should also contain the primary question or prompt for the user, such as asking if they want to solve a problem, explore more on a specific topic, or provide further assistance. This field should NOT immediately reveal the answer. Instead, guide the user towards finding the answer themselves. If the user fails multiple attempts, you may reveal the answer and provide a detailed explanation. If the user brings up a new topic while the assistant is waiting for a response to previous one, ask them if they want to switch to new topic before changing context.',
        },
        quickResponsePrompts: {
          type: 'array',
          items: { type: 'string' },
          description:
            'An optional array of strings representing predefined response options that the assistant can present as clickable buttons to the user. These quick responses allow the user to quickly continue the conversation by selecting one of the available options. When a user clicks one of these responses, it will be sent as their next message, enabling a faster and more interactive exchange. These should be tailored to the topic of discussion and may include follow-up questions or suggestions to deepen engagement or further explore the subject.',
        },
        ...(useLoadingTextParts
          ? {
              loadingTextParts: {
                type: 'array',
                items: { type: 'string' },
                description:
                  "A list of fun and/or curiosity-boosting placeholder texts related to the topic of discussion. These texts are displayed while the user waits for the AI assistant's response. The initial values come from a predefined list, and subsequent responses generate new texts dynamically based on the conversation theme.",
              },
            }
          : {}),
        ...(isRecommendationConversation && {
          problemSolved: {
            type: 'boolean',
            description:
              "MANDATORY for recommendation sessions: verdict on the student's latest answer to the current set problem. TRUE whenever the student produced the correct final answer - a bare correct answer counts, working is never required, and equivalent forms count. FALSE for wrong or partial answers, and FALSE on the opening message that presents the first problem. One TRUE banks one set problem; the client counts banked problems for the progress bar.",
          },
          isUserCompletePracticeQuestion: {
            type: 'boolean',
            description:
              'CRITICAL FOR RECOMMENDATION SESSIONS: the session is a fixed set of practice problems. Set to TRUE only when every problem in the set has been banked with problemSolved TRUE. Set to FALSE while any set problem remains unsolved, however good a single answer was. This field is MANDATORY for every response in recommendation conversations.',
          },
          completionReason: {
            type: 'string',
            description:
              'MANDATORY when isUserCompletePracticeQuestion is included: Brief explanation of why the practice is complete or incomplete. Examples: "Student solved the problem correctly and showed understanding" or "Student needs more practice with basic concepts".',
          },
        }),
      },
      required: [
        'text',
        ...(useLoadingTextParts ? ['loadingTextParts'] : []),
        ...(isRecommendationConversation
          ? [
              'problemSolved',
              'isUserCompletePracticeQuestion',
              'completionReason',
            ]
          : []),
      ],
    },
  }
}

// A long conversation title (e.g. a seeded Ask-Axi context) embedded raw into
// schema descriptions makes the json_schema payload big enough that every
// OpenAI model rejects the request (observed 8 Sep: all models failed -> the
// route 503s). Keep the embedded topic short and single-line.
const sanitizeSchemaTopic = (topic?: string): string =>
  (topic ?? '').replace(/\s+/g, ' ').trim().slice(0, 120)

export function generateAiAssistantJsonSchema(config: {
  currentTopic?: string
  isRecommendationConversation?: boolean
  useLoadingTextParts?: boolean
}): ResponseFormatJSONSchema {
  const {
    currentTopic: rawCurrentTopic,
    isRecommendationConversation,
    useLoadingTextParts = true,
  } = config

  const currentTopic = sanitizeSchemaTopic(rawCurrentTopic)

  return {
    type: 'json_schema',
    json_schema: {
      name: 'ai_assistant_response',
      strict: true,
      description: `You are a helpful AI assistant for a math tutoring app called Visely. Your role is to provide educational support to students by answering their questions clearly and helpfully. Always be encouraging and supportive. When discussing mathematical concepts, use proper mathematical notation and provide step-by-step explanations when appropriate. If the current conversation topic is provided: "${currentTopic}", try to relate your response to this topic when relevant.`,
      schema: {
        type: 'object',
        properties: {
          text: {
            type: 'string',
            description:
              'The main response content, providing an answer or explanation. This field is mandatory. This field should not contain any questions or prompts. This field should maintain the current context unless the student explicitly confirms switching topics.',
          },
          topic: {
            type: ['string', 'null'],
            description: `
										The general topic of discussion used as title for conversation, such as "Algebra" or "Calculus". 
											- This field is not mandatory and can be used as a title for a specific theme.
											- Always start this field with a capital letter.
											- If the topic shifts naturally within the conversation, update it accordingly while ensuring continuity.
											- ${
                        currentTopic
                          ? `The current topic is "${currentTopic}". If the discussion context changes, update this field to reflect the new focus accurately.`
                          : ''
                      }`,
          },
          visualRepresentation: {
            type: ['string', 'null'],
            description: `An optional LaTeX TikZ diagram that visually represents the concept or problem being explained.This should be included only when:
											- The user specifically asks for a diagram or visual.
											- A visual aid would significantly improve clarity or understanding.
										The diagram is rendered by TikZJax, a legacy TikZ engine with NO extra TikZ libraries available, so these rules are hard requirements: use ONLY classic arrow tips - write "->" or ">=stealth", NEVER ">=Latex", ">=Stealth" or any arrows.meta tip name; do NOT use pgfplots (no \\begin{axis} - plot curves with \\draw plot instead); do NOT use the positioning library "of" syntax like "below=4pt of P" - place nodes with explicit coordinates or "below of=P"; no \\usepackage and no \\usetikzlibrary; remember pgf math trig functions work in DEGREES (sin(90)=1) - for radian-valued curves write sin(\\x r) and keep every coordinate within about -20..20 so dimensions do not overflow. Output raw TikZ only: no markdown code fences, no captions or labels like "Diagram:" - just \\begin{tikzpicture}...\\end{tikzpicture}.`,
          },
          example: {
            type: ['string', 'null'],
            description:
              'An optional example problem and its solution to illustrate the concept. If provided, this will unlock additional fields such as tip and commonMistake.For better readability, especially with long expressions, break them into smaller parts to prevent overflow.',
          },
          tip: {
            type: ['string', 'null'],
            description:
              'An optional helpful strategy or technique for understanding or solving related problems. Shown only if "example" is provided.',
          },
          commonMistake: {
            type: ['string', 'null'],
            description:
              'An optional common error students make and how to avoid it. Shown only if "example" is provided.',
          },
          followUpQuestion: {
            type: ['string', 'null'],
            description:
              'An additional question to reinforce learning and encourage engagement. This field should also contain the primary question or prompt for the user, such as asking if they want to solve a problem, explore more on a specific topic, or provide further assistance. This field should NOT immediately reveal the answer. Instead, guide the user towards finding the answer themselves. If the user fails multiple attempts, you may reveal the answer and provide a detailed explanation. If the user brings up a new topic while the assistant is waiting for a response to previous one, ask them if they want to switch to new topic before changing context.',
          },
          quickResponsePrompts: {
            type: ['array', 'null'],
            items: { type: 'string' },
            description:
              'An optional array of strings representing predefined response options that the assistant can present as clickable buttons to the user. These quick responses allow the user to quickly continue the conversation by selecting one of the available options. When a user clicks one of these responses, it will be sent as their next message, enabling a faster and more interactive exchange. These should be tailored to the topic of discussion and may include follow-up questions or suggestions to deepen engagement or further explore the subject.',
          },
          ...(useLoadingTextParts
            ? {
                loadingTextParts: {
                  type: ['array', 'null'],
                  items: { type: 'string' },
                  description:
                    "A list of fun and/or curiosity-boosting placeholder texts related to the topic of discussion. These texts are displayed while the user waits for the AI assistant's response. The initial values come from a predefined list, and subsequent responses generate new texts dynamically based on the conversation theme.",
                },
              }
            : {}),
          ...(isRecommendationConversation && {
            problemSolved: {
              type: ['boolean', 'null'],
              description:
                "MANDATORY for recommendation sessions: verdict on the student's latest answer to the current set problem. TRUE whenever the student produced the correct final answer - a bare correct answer counts, working is never required, and equivalent forms count. FALSE for wrong or partial answers, and FALSE on the opening message that presents the first problem. One TRUE banks one set problem; the client counts banked problems for the progress bar.",
            },
            isUserCompletePracticeQuestion: {
              type: ['boolean', 'null'],
              description:
                'CRITICAL FOR RECOMMENDATION SESSIONS: the session is a fixed set of practice problems. Set to TRUE only when every problem in the set has been banked with problemSolved TRUE. Set to FALSE while any set problem remains unsolved, however good a single answer was. This field is MANDATORY for every response in recommendation conversations.',
            },
            completionReason: {
              type: ['string', 'null'],
              description:
                'MANDATORY when isUserCompletePracticeQuestion is included: Brief explanation of why the practice is complete or incomplete. Examples: "Student solved the problem correctly and showed understanding" or "Student needs more practice with basic concepts".',
            },
          }),
        },
        additionalProperties: false,
        required: [
          'text',
          'topic',
          'visualRepresentation',
          'example',
          'tip',
          'commonMistake',
          'followUpQuestion',
          'quickResponsePrompts',
          ...(useLoadingTextParts ? ['loadingTextParts'] : []),
          ...(isRecommendationConversation
            ? [
                'problemSolved',
                'isUserCompletePracticeQuestion',
                'completionReason',
              ]
            : []),
        ],
      },
    },
  }
}

export function generatePostExamFunctionSchema(): Record<string, any> {
  return {
    name: 'post_exam_tutor_response',
    type: 'object',
    description:
      'The assistant is an AI tutor on an educational platform where students take simulated exams and then receive personalized feedback and guided learning. Its purpose is to help the student review their exam performance, clarify misunderstandings, and strengthen their knowledge through interactive, supportive conversation. Responses should focus on explaining concepts, addressing weak areas, and guiding the student toward deeper understanding - not simply giving direct answers. The assistant may ask reflective or quiz-style questions to encourage active learning and reasoning. Return an object matching the schema. "tip" and "commonMistake" are optional and should only be included when the assistant is explaining a concept or theory (never during problem-solving or general dialogue).',
    parameters: {
      type: 'object',
      additionalProperties: false,
      properties: {
        text: {
          type: 'string',
          description:
            'The main textual response from the tutor. Keep it concise and actionable.',
        },
        tip: {
          type: 'string',
          nullable: true,
          description:
            'Optional. A short, practical study tip that directly relates to the topic, concept, or theory being explained. Use ONLY when the assistant is clarifying or teaching conceptual material - never during practice tasks or general dialogue.',
        },
        commonMistake: {
          type: 'string',
          nullable: true,
          description:
            'Optional. A brief explanation of a common conceptual misunderstanding related to the topic being discussed. Use ONLY when explaining theory - not during calculations, exercises, or practice scenarios.',
        },
        quickResponsePrompts: {
          type: 'array',
          maxItems: 3,
          items: {
            type: 'string',
            maxLength: 80,
          },
          description:
            '2-4 short action prompts (<=5 words) to render as clickable buttons. Do NOT include prompts that reveal answers when the student should attempt solving first.',
        },
      },
      required: ['text'],
    },
  }
}

export function generatePostExamJsonSchema(): ResponseFormatJSONSchema {
  return {
    type: 'json_schema',
    json_schema: {
      name: 'post_exam_tutor_response',
      strict: true,
      description:
        'The assistant is an AI tutor on an educational platform where students take simulated exams and then receive personalized feedback and guided learning. Its purpose is to help the student review their exam performance, clarify misunderstandings, and strengthen their knowledge through interactive, supportive conversation. Responses should focus on explaining concepts, addressing weak areas, and guiding the student toward deeper understanding - not simply giving direct answers. The assistant may ask reflective or quiz-style questions to encourage active learning and reasoning. Return an object matching the schema. "tip" and "commonMistake" are optional and should only be included when the assistant is explaining a concept or theory (never during problem-solving or general dialogue).',
      schema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          text: {
            type: 'string',
            description:
              'The main textual response from the tutor. Keep it concise and actionable.',
          },
          tip: {
            type: ['string', 'null'],
            description:
              'Optional. A short, practical study tip that directly relates to the topic, concept, or theory being explained. Use ONLY when the assistant is clarifying or teaching conceptual material - never during practice tasks or general dialogue.',
          },
          commonMistake: {
            type: ['string', 'null'],
            description:
              'Optional. A brief explanation of a common conceptual misunderstanding related to the topic being discussed. Use ONLY when explaining theory - not during calculations, exercises, or practice scenarios.',
          },
          quickResponsePrompts: {
            type: ['array', 'null'],
            items: {
              type: 'string',
            },
            description:
              '2-4 short action prompts (<=5 words) to render as clickable buttons. Do NOT include prompts that reveal answers when the student should attempt solving first.',
          },
        },
        required: ['text', 'tip', 'commonMistake', 'quickResponsePrompts'],
      },
    },
  }
}

export function generateMultiModeFunctionSchema(config: {
  chatMode?: LessonChatMode
  attemptCount?: number
  givenHintCount?: number
  currentQuestion?: string
}) {
  const {
    chatMode,
    attemptCount = 0,
    givenHintCount = 0,
    currentQuestion = '',
  } = config

  const generalDescription = `
	Current Chat Mode: ${chatMode}.
	Attempts Count: ${attemptCount}.

		- As an AI Tutor in ${LessonChatMode.Assessment} Mode, provide encouraging and constructive feedback that builds on the student's previous work and understanding. Evaluate answers through a growth mindset lens, focusing on progress and learning opportunities. Return a JSON response with: gptResponseToStudent: A warm, personalized message that acknowledges effort and guides learning. isCompleted: Boolean indicating if learning objectives are met for the last question. For incorrect answers: Guide with scaffolded support, building on existing understanding and previous attempts. For correct answers: Validate their reasoning and connect to broader concepts. Never reveal solutions before meaningful engagement, even partially. Assume mathematical competence unless steps are explicitly requested. Grade as Correct, Incorrect, or Partially Correct while maintaining encouraging, growth-focused feedback.

		- As an AI Tutor in ${LessonChatMode.Learning} Mode, your primary task is to act as an instructor, providing in-depth teaching and comprehensive explanations related to the current lesson's question. Focus on expanding the student's understanding by offering clear examples, highlighting common mistakes, and sharing helpful tips. Encourage active exploration and clarification of the topic through unlimited interaction. The goal is to ensure the student fully grasps the subject matter and can independently solve similar problems, rather than strictly grading their answers. Build upon the student's previous interactions to provide personalized and contextualized learning.
	`

  return {
    type: 'object',
    name: 'multi_chat_mode_grade_task',
    description: generalDescription,
    parameters: {
      type: 'object',
      properties: {
        gptResponseToStudent: {
          type: 'string',
          description: `Craft personalized feedback that acknowledges effort and guides learning. Use proper KaTeX formatting ($...$ with double backslashes \\\\) for mathematical expressions. Build on previous interactions and understanding. For unrelated answers, gently redirect to core concepts. Avoid direct quotes of student work, instead reformulating with correct notation.`,
        },
        points: {
          type: 'string',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Award points on a 5-point scale (X/5) based on demonstrated understanding and progress of the current question only: ${currentQuestion}. Consider partial credit for meaningful attempts and logical reasoning of the current question. Award full points (5/5) for complete, accurate solutions of the current attempt. If the response is unrelated or seeks guidance without attempt or is referring to previous conversation, withhold points while maintaining encouragement.`
              : `Set '0/5'`,
        },
        isCompleted: {
          type: 'boolean',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Set 'true' when learning objectives are met through: meaningful engagement (${givenHintCount} ≥ 3), persistent effort (${attemptCount} ≥ 3), or demonstrated mastery (points = 5/5). Otherwise 'false'. Withhold final solution until completion criteria met of the last question. If he has not given the answer set this as false`
              : `Set 'false'`,
        },
        isAnswered: {
          type: 'boolean',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Set this to 'true' ONLY if the student's answer is mathematically equivalent to the correct solution. Consider different forms (e.g. factored vs expanded) as correct if they are algebraically equivalent. For numerical answers, allow reasonable rounding differences. For proofs, require logical equivalence. For multi-part questions, such as parts (a) and (b), the student must correctly respond to all required parts to receive a 'true'. Answers phrased with uncertainty (a trailing question mark, "maybe", "I think", "is it ...?") still earn 'true' when the stated answer itself is correct - never withhold 'true' for hesitant phrasing; instead, briefly note the uncertainty in your feedback as a confidence nudge.
							Also, set 'true' only if answer is correct AND either: 
								a) Question doesn't require working steps, OR 
								b) Question contains "show your working"/"show steps" AND working is provided`
              : `Set 'false'`,
        },
        gptResponseToStudentText: {
          type: 'string',
          description: `Convert mathematical feedback into natural, spoken language. Transform KaTeX notation into clear verbal descriptions (e.g., '$\\\\frac{1}{2}$' becomes 'one half'). Maintain supportive tone while ensuring mathematical clarity.`,
        },
        isPartiallyCorrect: {
          type: 'boolean',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Recognize partial understanding and progress. Consider both explicit solutions and implicit understanding. Acknowledge valid approaches even if final answer isn't perfect. The current question is ${currentQuestion}`
              : `Set 'false'`,
        },
        incrementAttemptCount: {
          type: 'boolean',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Track substantive solution attempts. Set 'true' for direct answer attempts, 'false' for clarification requests, hint seeking, or tangential responses.`
              : `Set 'false'`,
        },
        incrementGivenHintCount: {
          type: 'boolean',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Track meaningful guidance provided. Set 'true' when offering substantive hints or addressing core concept questions. Set 'false' for general clarifications or non-hint responses.`
              : `Set 'false'`,
        },
        studentAnswerIncludesKaTexSyntax: {
          type: 'boolean',
          description:
            "Identify if last students answer includes KaTeX syntax. Set 'true' if mathematical notation present, 'false' otherwise. Ensures proper handling of mathematical expressions in responses.",
        },
        isHintRequest: {
          type: 'boolean',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Set 'true' if the student's question requests problem-solving help, hints, or guidance specific to answering the question. Set 'false' for general course/procedure questions, clarification requests, or non-content inquiries.`
              : `Set 'false'`,
        },
        isSolutionResponse: {
          type: 'boolean',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Set to true if this response reveals step by step solution requested with "Request for solution pattern" ("${solutionRequestText}").`
              : `Set 'false'`,
        },
        isLastQuestionCompleted: {
          type: 'boolean',
          description:
            chatMode === LessonChatMode.Assessment
              ? `Keep 'true' if the last question in conversation was completed, and new one or similar not provided yet. This also includes cases where:
								- The AI Tutor confirms that the student's answer for the last question is correct.
								- The student answered the last question correctly.
								- The AI Tutor reveals the correct answer, the correct answer with solution, or the correct answer with reasoning for the last question.

								Set 'false' immediately when a new question is provided. This includes cases where:
								- The correct answer was not provided by student for the last question in conversation.
								- The student requests for a new question or similar question.
								- You generate a new or similar question for the student.
								- You generate a new follow-up question for the student and question is not answered yet (e.g. isAnswered is set to false).`
              : `Set 'false'`,
        },
        tip: {
          type: 'string',
          description: `Provide a helpful strategy or shortcut for similar problems. Use when: 1) There's a reusable problem-solving approach, 2) A particular technique simplifies solutions, 3) It helps generalize the learning. Format: "For similar problems, try [concrete action] because [brief rationale]." Only include when directly relevant to the solution and isSolutionResponse is set to "true" or current chat mode is ${LessonChatMode.Learning}.`,
        },
        commonMistake: {
          type: 'string',
          description: `Describe typical errors students make with this problem. Use when: 1) There's a common misconception, 2) The error pattern recurs frequently, 3) It helps prevent future mistakes. Format: "Students often [specific error] because [reason]. Remember that [correction]." Include only for solution responses when isSolutionResponse is set to "true" or current chat mode is ${LessonChatMode.Learning}.`,
        },
        quickResponsePrompts: {
          type: 'array',
          items: { type: 'string' },
          description:
            chatMode === LessonChatMode.Assessment
              ? `Return []`
              : `An optional array of 1-3 short, clickable response options (max 5-7 words each). These prompts should be generated according to the detailed rules provided in the main prompt under the "Quick Response Prompts" section. The main prompt contains all the necessary instructions on what to include, what to prioritize, and what to avoid.`,
        },
        isChatModeSwitchProposed: {
          type: 'boolean',
          description: `Set 'true' if the AI Tutor **proposes** a chat mode switch. 
					- **From ${LessonChatMode.Assessment} Mode to ${LessonChatMode.Learning} Mode:** Set 'true' when the student is struggling with the current problem (e.g., after multiple incorrect attempts, or showing significant conceptual gaps), and the AI Tutor explicitly suggests switching to Learning Mode for foundational concept explanation and practice. 
					- **From ${LessonChatMode.Learning} Mode to ${LessonChatMode.Assessment} Mode:** Set 'true' when the student has successfully demonstrated a strong grasp of the concepts being taught in Learning Mode (e.g., by correctly answering embedded practice questions, clarifying understanding), and the AI Tutor explicitly suggests switching back to Assessment Mode to apply their knowledge.
					- This field should be set to 'true' **only** if the suggestion for a mode switch is explicitly made in the 'gptResponseToStudent' field. Otherwise - 'false'.`,
        },
        confidence: {
          type: 'number',
          description: `Student confidence coefficient (0-1) representing their understanding level. Evaluate based on: 1) Answer correctness and quality, 2) Problem-solving approach demonstrated, 3) Conceptual understanding shown, 4) Independence vs reliance on hints/solutions. Higher values (0.8-1.0) for correct answers with clear reasoning. Medium values (0.4-0.7) for partial understanding or answers requiring hints. Lower values (0.0-0.3) for significant struggles or incorrect approaches. Consider conversation context and learning progress.`,
        },
      },
      required: [
        'gptResponseToStudent',
        'points',
        'isCompleted',
        'isAnswered',
        'gptResponseToStudentText',
        'isPartiallyCorrect',
        'incrementAttemptCount',
        'incrementGivenHintCount',
        'studentAnswerIncludesKaTexSyntax',
        'isHintRequest',
        'isSolutionResponse',
        'isLastQuestionCompleted',
        'isChatModeSwitchProposed',
        'confidence',
      ],
    },
  }
}

export function generateMultiModeJsonSchema(config: {
  chatMode: LessonChatMode
  attemptCount: number
  givenHintCount: number
  currentQuestion: string
  isSolutionRequest?: boolean
}): ResponseFormatJSONSchema {
  const {
    chatMode,
    attemptCount = 0,
    givenHintCount = 0,
    isSolutionRequest = false,
  } = config

  let description = `
		Current Chat Mode: ${chatMode}.
		Attempts Count: ${attemptCount}.
	`

  if (chatMode === LessonChatMode.Assessment) {
    description += `
		- As an AI Tutor in ${LessonChatMode.Assessment} Mode, provide encouraging and constructive feedback that builds on the student's previous work and understanding. Evaluate answers through a growth mindset lens, focusing on progress and learning opportunities. Return a JSON response with: gptResponseToStudent: A warm, personalized message that acknowledges effort and guides learning. isCompleted: Boolean indicating if learning objectives are met for the last question. For incorrect answers: Guide with scaffolded support, building on existing understanding and previous attempts. For correct answers: Validate their reasoning and connect to broader concepts. Never reveal solutions before meaningful engagement, even partially. Assume mathematical competence unless steps are explicitly requested. Grade as Correct, Incorrect, or Partially Correct while maintaining encouraging, growth-focused feedback.`
  } else if (chatMode === LessonChatMode.Learning) {
    description += `
		- As an AI Tutor in ${LessonChatMode.Learning} Mode, your primary task is to act as an instructor, providing in-depth teaching and comprehensive explanations related to the current lesson's question. Focus on expanding the student's understanding by offering clear examples, highlighting common mistakes, and sharing helpful tips. Encourage active exploration and clarification of the topic through unlimited interaction. The goal is to ensure the student fully grasps the subject matter and can independently solve similar problems, rather than strictly grading their answers. Build upon the student's previous interactions to provide personalized and contextualized learning.`
  }

  return {
    type: 'json_schema',
    json_schema: {
      name: 'multi_chat_mode_grade_task',
      description,
      strict: true,
      schema: {
        type: 'object',
        properties: {
          gptResponseToStudent: {
            type: 'string',
            description: `Craft personalized feedback that acknowledges effort and guides learning. Use proper KaTeX formatting ($...$ with double backslashes \\\\) for mathematical expressions. Build on previous interactions and understanding. For unrelated answers, gently redirect to core concepts. Avoid direct quotes of student work, instead reformulating with correct notation.`,
          },
          points: {
            type: 'string',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Award points on a 5-point scale (X/5) based on demonstrated understanding and progress of the current question only. Consider partial credit for meaningful attempts and logical reasoning of the current question. Award full points (5/5) for complete, accurate solutions of the current attempt. If the response is unrelated or seeks guidance without attempt or is referring to previous conversation, withhold points while maintaining encouragement.`
                : `Set '0/5'`,
          },
          isCompleted: {
            type: 'boolean',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Set 'true' when learning objectives are met through: meaningful engagement (${givenHintCount} ≥ 3), persistent effort (${attemptCount} ≥ 3), or demonstrated mastery (points = 5/5). Otherwise 'false'. Withhold final solution until completion criteria met of the last question. If he has not given the answer set this as false`
                : `Set 'false'`,
          },
          isAnswered: {
            type: 'boolean',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Set this to 'true' ONLY if the student's answer is mathematically equivalent to the correct solution. Consider different forms (e.g. factored vs expanded) as correct if they are algebraically equivalent. For numerical answers, allow reasonable rounding differences. For proofs, require logical equivalence. For multi-part questions, such as parts (a) and (b), the student must correctly respond to all required parts to receive a 'true'. Answers phrased with uncertainty (a trailing question mark, "maybe", "I think", "is it ...?") still earn 'true' when the stated answer itself is correct - never withhold 'true' for hesitant phrasing; instead, briefly note the uncertainty in your feedback as a confidence nudge.
							Also, set 'true' only if answer is correct AND either: 
								a) Question doesn't require working steps, OR 
								b) Question contains "show your working"/"show steps" AND working is provided`
                : `Set 'false'`,
          },
          gptResponseToStudentText: {
            type: 'string',
            description: `Convert mathematical feedback into natural, spoken language. Transform KaTeX notation into clear verbal descriptions (e.g., '$\\\\frac{1}{2}$' becomes 'one half'). Maintain supportive tone while ensuring mathematical clarity.`,
          },
          isPartiallyCorrect: {
            type: 'boolean',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Recognize partial understanding and progress. Consider both explicit solutions and implicit understanding. Acknowledge valid approaches even if final answer isn't perfect.`
                : `Set 'false'`,
          },
          incrementAttemptCount: {
            type: 'boolean',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Track substantive solution attempts. Set 'true' for direct answer attempts, 'false' for clarification requests, hint seeking, or tangential responses.`
                : `Set 'false'`,
          },
          incrementGivenHintCount: {
            type: 'boolean',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Track meaningful guidance provided. Set 'true' when offering substantive hints or addressing core concept questions. Set 'false' for general clarifications or non-hint responses.`
                : `Set 'false'`,
          },
          studentAnswerIncludesKaTexSyntax: {
            type: 'boolean',
            description:
              "Identify if last students answer includes KaTeX syntax. Set 'true' if mathematical notation present, 'false' otherwise. Ensures proper handling of mathematical expressions in responses.",
          },
          isHintRequest: {
            type: 'boolean',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Set 'true' if the student's question requests problem-solving help, hints, or guidance specific to answering the question. Set 'false' for general course/procedure questions, clarification requests, or non-content inquiries.`
                : `Set 'false'`,
          },
          isSolutionResponse: {
            type: 'boolean',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Set to true if this response reveals step by step solution requested with "Request for solution pattern" ("${solutionRequestText}").`
                : `Set 'false'`,
          },
          isLastQuestionCompleted: {
            type: 'boolean',
            description:
              chatMode === LessonChatMode.Assessment
                ? `Keep 'true' if the last question in conversation was completed, and new one or similar not provided yet. This also includes cases where:
								- The AI Tutor confirms that the student's answer for the last question is correct.
								- The student answered the last question correctly.
								- The AI Tutor reveals the correct answer, the correct answer with solution, or the correct answer with reasoning for the last question.

								Set 'false' immediately when a new question is provided. This includes cases where:
								- The correct answer was not provided by student for the last question in conversation.
								- The student requests for a new question or similar question.
								- You generate a new or similar question for the student.
								- You generate a new follow-up question for the student and question is not answered yet (e.g. isAnswered is set to false).`
                : `Set 'false'`,
          },
          isChatModeSwitchProposed: {
            type: 'boolean',
            description: `Set 'true' if the AI Tutor **proposes** a chat mode switch. 
							- **From ${LessonChatMode.Assessment} Mode to ${LessonChatMode.Learning} Mode:** Set 'true' when the student is struggling with the current problem (e.g., after multiple incorrect attempts, or showing significant conceptual gaps), and the AI Tutor explicitly suggests switching to Learning Mode for foundational concept explanation and practice. 
							- **From ${LessonChatMode.Learning} Mode to ${LessonChatMode.Assessment} Mode:** Set 'true' when the student has successfully demonstrated a strong grasp of the concepts being taught in Learning Mode (e.g., by correctly answering embedded practice questions, clarifying understanding), and the AI Tutor explicitly suggests switching back to Assessment Mode to apply their knowledge.
							- This field should be set to 'true' **only** if the suggestion for a mode switch is explicitly made in the 'gptResponseToStudent' field. Otherwise - 'false'.`,
          },
          confidence: {
            type: 'number',
            description: `Student confidence coefficient (0-1) representing their understanding level. Evaluate based on: 1) Answer correctness and quality, 2) Problem-solving approach demonstrated, 3) Conceptual understanding shown, 4) Independence vs reliance on hints/solutions. Higher values (0.8-1.0) for correct answers with clear reasoning. Medium values (0.4-0.7) for partial understanding or answers requiring hints. Lower values (0.0-0.3) for significant struggles or incorrect approaches. Consider conversation context and learning progress.`,
          },
          // SolutionRequest or Learning mode fields
          ...(isSolutionRequest || chatMode === LessonChatMode.Learning
            ? {
                tip: {
                  type: ['string', 'null'],
                  description: `Provide a helpful strategy or shortcut for similar problems. Use when: 1) There's a reusable problem-solving approach, 2) A particular technique simplifies solutions, 3) It helps generalize the learning. Format: "For similar problems, try [concrete action] because [brief rationale]." Only include when directly relevant to the solution and isSolutionResponse is set to "true" or current chat mode is ${LessonChatMode.Learning}.`,
                },
                commonMistake: {
                  type: ['string', 'null'],
                  description: `Describe typical errors students make with this problem. Use when: 1) There's a common misconception, 2) The error pattern recurs frequently, 3) It helps prevent future mistakes. Format: "Students often [specific error] because [reason]. Remember that [correction]." Include only for solution responses when isSolutionResponse is set to "true" or current chat mode is ${LessonChatMode.Learning}.`,
                },
              }
            : {}),
          // Learning mode fields
          ...(chatMode === LessonChatMode.Learning
            ? {
                quickResponsePrompts: {
                  type: 'array',
                  items: { type: 'string' },
                  description: `An optional array of 1-3 short, clickable response options (max 5-7 words each). These prompts should be generated according to the detailed rules provided in the main prompt under the "Quick Response Prompts" section. The main prompt contains all the necessary instructions on what to include, what to prioritize, and what to avoid.`,
                },
              }
            : {}),
        },
        additionalProperties: false,
        required: [
          'gptResponseToStudent',
          'points',
          'isCompleted',
          'isAnswered',
          'gptResponseToStudentText',
          'isPartiallyCorrect',
          'incrementAttemptCount',
          'incrementGivenHintCount',
          'studentAnswerIncludesKaTexSyntax',
          'isHintRequest',
          'isSolutionResponse',
          'isLastQuestionCompleted',
          'isChatModeSwitchProposed',
          'confidence',
          ...(isSolutionRequest || chatMode === LessonChatMode.Learning
            ? ['tip', 'commonMistake']
            : []),
          ...(chatMode === LessonChatMode.Learning
            ? ['quickResponsePrompts']
            : []),
        ],
      },
    },
  }
}

export function getRegularLessonJsonSchema(config: {
  attemptCount?: number
  givenHintCount?: number
  currentQuestion: string
}): ResponseFormatJSONSchema {
  const { attemptCount = 0, givenHintCount = 0 } = config

  return {
    type: 'json_schema',
    json_schema: {
      name: 'grade_task',
      description: `As an AI tutor, provide encouraging and constructive feedback that builds on the student's previous work and understanding. Evaluate answers through a growth mindset lens, focusing on progress and learning opportunities. Return a JSON response with: gptResponseToStudent: A warm, personalized message that acknowledges effort and guides learning. isCompleted: Boolean indicating if learning objectives are met for the last question. For incorrect answers: Guide with scaffolded support, building on existing understanding and previous attempts. Never reveal solutions before meaningful engagement. Assume mathematical competence unless steps are explicitly requested. Grade as Correct, Incorrect, or Partially Correct while maintaining encouraging, growth-focused feedback.`,
      strict: true, // means all fields are required
      schema: {
        type: 'object',
        properties: {
          gptResponseToStudent: {
            type: 'string',
            description:
              'Craft personalized feedback that acknowledges effort and guides learning. Use proper KaTeX formatting ($...$ with double backslashes \\\\) for mathematical expressions. Build on previous interactions and understanding. For unrelated answers, gently redirect to core concepts. Avoid direct quotes of student work, instead reformulating with correct notation.',
          },
          points: {
            type: 'string',
            description: `Award points on a 5-point scale (X/5) based on demonstrated understanding and progress of the current question only. Consider partial credit for meaningful attempts and logical reasoning of the current question. Award full points (5/5) for complete, accurate solutions of the current attempt. If the response is unrelated or seeks guidance without attempt or is referring to previous conversation, withhold points while maintaining encouragement.`,
          },
          isCompleted: {
            type: 'boolean',
            description: `Set 'true' when learning objectives are met through: meaningful engagement (${givenHintCount} ≥ 3), persistent effort (${attemptCount} ≥ 3), or demonstrated mastery (points = 5/5). Otherwise 'false'. Withhold final solution until completion criteria met of the last question. If he has not given the answer set this as false`,
          },
          isAnswerRelevant: {
            type: 'boolean',
            description:
              "Assess alignment with core question objectives. Set 'true' for responses that engage with the fundamental concepts, even if imperfectly. Consider previous context and build on prior understanding.",
          },
          answerRelevantDescription: {
            type: ['string', 'null'],
            description:
              "Provide detailed analysis of relevance, comparing student's approach with solution path. Include specific examples from student work and solution for transparent evaluation. Focus on understanding demonstrated rather than just correctness.",
          },
          isAnswered: {
            type: 'boolean',
            description: `Set this to 'true' ONLY if the student's answer is mathematically equivalent to the correct solution. Consider different forms (e.g. factored vs expanded) as correct if they are algebraically equivalent. For numerical answers, allow reasonable rounding differences. For proofs, require logical equivalence. For multi-part questions, such as parts (a) and (b), the student must correctly respond to all required parts to receive a 'true'. Answers phrased with uncertainty (a trailing question mark, "maybe", "I think", "is it ...?") still earn 'true' when the stated answer itself is correct - never withhold 'true' for hesitant phrasing; instead, briefly note the uncertainty in your feedback as a confidence nudge. Also, set 'true' only if answer is correct AND either: a) Question doesn't require working steps, OR b) Question contains "show your working"/"show steps" AND working is provided`,
          },
          studentAnswerIncludesKaTexSyntax: {
            type: 'boolean',
            description:
              "Identify KaTeX syntax in user message. Set 'true' if mathematical notation present, 'false' otherwise. Ensures proper handling of mathematical expressions in responses.",
          },
          isPartiallyCorrect: {
            type: 'boolean',
            description: `Recognize partial understanding and progress. Consider both explicit solutions and implicit understanding. Acknowledge valid approaches even if final answer isn't perfect.`,
          },
          incrementGivenHintCount: {
            type: 'boolean',
            description:
              "Track meaningful guidance provided. Set 'true' when offering substantive hints or addressing core concept questions. 'False' for general clarifications or non-hint responses.",
          },
          incrementAttemptCount: {
            type: 'boolean',
            description:
              "Track substantive solution attempts. Set 'true' for direct answer attempts, 'false' for clarification requests, hint seeking, or tangential responses.",
          },
          gptResponseToStudentText: {
            type: ['string', 'null'],
            description:
              "Convert mathematical feedback into natural, spoken language. Transform KaTeX notation into clear verbal descriptions (e.g., '$\\\\frac{1}{2}$' becomes 'one half'). Maintain supportive tone while ensuring mathematical clarity.",
          },
          isHintRequest: {
            type: 'boolean',
            description: `Set 'true' if the student's question requests problem-solving help, hints, or guidance specific to answering the question. Set 'false' for general course/procedure questions, clarification requests, or non-content inquiries.`,
          },
          isLastQuestionCompleted: {
            type: 'boolean',
            description: `Keep 'true' if the last question in conversation was completed, and new one or similar not provided yet. This also includes cases where: - The tutor confirms that the student's answer for the last question is correct. - The student answered the last question correctly. - The tutor reveals the correct answer, the correct answer with solution, or the correct answer with reasoning for the last question. Set 'false' immediately when a new question is provided. This includes cases where: - The correct answer was not provided by student for the last question in conversation. - The student requests for a new question or similar question. - You generate a new or similar question for the student. - You generate a new follow-up question for the student.`,
          },
          isSolutionResponse: {
            type: 'boolean',
            description: `Set to true if this response reveals step by step solution requested with "Request for solution pattern".`,
          },
          tip: {
            type: ['string', 'null'],
            description: `Provide a helpful strategy or shortcut for similar problems. Use when: 1) There's a reusable problem-solving approach, 2) A particular technique simplifies solutions, 3) It helps generalize the learning. Format: "For similar problems, try [concrete action] because [brief rationale]." Only include when directly relevant to the solution and isSolutionResponse is set to "true".`,
          },
          commonMistake: {
            type: ['string', 'null'],
            description: `Describe typical errors students make with this problem. Use when: 1) There's a common misconception, 2) The error pattern recurs frequently, 3) It helps prevent future mistakes. Format: "Students often [specific error] because [reason]. Remember that [correction]." Include only for solution responses when isSolutionResponse is set to "true".`,
          },
          confidence: {
            type: 'number',
            description: `Student confidence coefficient (0-1) representing their understanding level. Evaluate based on: 1) Answer correctness and quality, 2) Problem-solving approach demonstrated, 3) Conceptual understanding shown, 4) Independence vs reliance on hints/solutions. Higher values (0.8-1.0) for correct answers with clear reasoning. Medium values (0.4-0.7) for partial understanding or answers requiring hints. Lower values (0.0-0.3) for significant struggles or incorrect approaches. Consider conversation context and learning progress.`,
          },
        },
        additionalProperties: false,
        required: [
          'gptResponseToStudent',
          'points',
          'isCompleted',
          'isAnswerRelevant',
          'answerRelevantDescription',
          'isAnswered',
          'studentAnswerIncludesKaTexSyntax',
          'isPartiallyCorrect',
          'incrementGivenHintCount',
          'incrementAttemptCount',
          'gptResponseToStudentText',
          'isHintRequest',
          'isLastQuestionCompleted',
          'isSolutionResponse',
          'tip',
          'commonMistake',
          'confidence',
        ],
      },
    },
  }
}

export function generateLessonWithMarksAvailableJsonSchema({
  shouldProcessMarks,
  totalMarks = 0,
  currentQuestion = '',
  givenHintCount = 0,
  attemptCount = 0,
  isSolutionRequest,
  schemeFlagged = false,
}: {
  shouldProcessMarks: boolean
  totalMarks: number
  currentQuestion: string
  givenHintCount: number
  attemptCount: number
  isSolutionRequest?: boolean
  schemeFlagged?: boolean
}): ResponseFormatJSONSchema {
  return {
    type: 'json_schema',
    json_schema: {
      strict: true, // means all fields are required
      name: 'grade_task_w_marks_available',
      description: `Structured grading response for a student answer, including feedback, correctness flags, and optional marks breakdown.${
        shouldProcessMarks
          ? ` Assess the student's answer against the mark scheme criteria and award marks out of the total available ${totalMarks} marks for this question. Use the mark scheme to determine exactly how many marks were earned for each point addressed in the answer.${schemeCheckInstruction(
              schemeFlagged,
            )}`
          : ''
      }`,
      schema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          // Solved before the feedback so the verdict follows the maths
          ...(shouldProcessMarks
            ? { independentAnswer: schemeCheckProperties.independentAnswer }
            : {}),
          gptResponseToStudent: {
            type: 'string',
            description:
              'Craft personalized feedback that acknowledges effort and guides learning. For mathematical expressions, use SIMPLE LaTeX formatting with single backslashes and prefer inline math ($x = 5$) over display math when possible. For complex formulas, use clear single-line expressions like $\\frac{a}{b}$ rather than multi-line displays. This ensures better readability during streaming. Build on previous interactions and understanding. For unrelated answers, gently redirect to core concepts. Avoid direct quotes of student work, instead reformulating with correct notation.' +
              (shouldProcessMarks
                ? ' For interactive papers, add detailed breakdown of the awarded marks.'
                : ''),
          },
          points: {
            type: 'string',
            description: `Award points on a 5-point scale (X/5) based on demonstrated understanding and progress of the current question only. Consider partial credit for meaningful attempts and logical reasoning of the current question. Award full points (5/5) for complete, accurate solutions of the current attempt. If the response is unrelated or seeks guidance without attempt or is referring to previous conversation, withhold points while maintaining encouragement.`,
          },
          isCompleted: {
            type: 'boolean',
            description: `Set 'true' when learning objectives are met through: meaningful engagement (${givenHintCount} ≥ 3), persistent effort (${attemptCount} ≥ 3), or demonstrated mastery (points = 5/5). Otherwise 'false'. Withhold final solution until completion criteria met of the last question. If he has not given the answer set this as false`,
          },
          isAnswerRelevant: {
            type: 'boolean',
            description:
              "Assess alignment with core question objectives. Set 'true' for responses that engage with the fundamental concepts, even if imperfectly. Consider previous context and build on prior understanding.",
          },
          answerRelevantDescription: {
            type: 'string',
            description:
              "Provide detailed analysis of relevance, comparing student's approach with solution path. Include specific examples from student work and solution for transparent evaluation. Focus on understanding demonstrated rather than just correctness.",
          },
          isAnswered: {
            type: 'boolean',
            description: `Set this to 'true' ONLY if the student's answer is mathematically equivalent to the correct solution. Consider different forms (e.g. factored vs expanded) as correct if they are algebraically equivalent. For numerical answers, allow reasonable rounding differences. For proofs, require logical equivalence. For multi-part questions, such as parts (a) and (b), the student must correctly respond to all required parts to receive a 'true'. Answers phrased with uncertainty (a trailing question mark, "maybe", "I think", "is it ...?") still earn 'true' when the stated answer itself is correct - never withhold 'true' for hesitant phrasing; instead, briefly note the uncertainty in your feedback as a confidence nudge. Also, set 'true' only if answer is correct AND either: a) Question doesn't require working steps, OR b) Question contains "show your working"/"show steps" AND working is provided`,
          },
          studentAnswerIncludesKaTexSyntax: {
            type: 'boolean',
            description:
              "Identify if last students answer includes KaTeX syntax. Set 'true' if mathematical notation present, 'false' otherwise. Ensures proper handling of mathematical expressions in responses.",
          },
          isPartiallyCorrect: {
            type: 'boolean',
            description: `Recognize partial understanding and progress. Consider both explicit solutions and implicit understanding. Acknowledge valid approaches even if final answer isn't perfect.`,
          },
          incrementGivenHintCount: {
            type: 'boolean',
            description:
              "Track meaningful guidance provided. Set 'true' when offering substantive hints or addressing core concept questions. 'False' for general clarifications or non-hint responses.",
          },
          incrementAttemptCount: {
            type: 'boolean',
            description:
              "Track substantive solution attempts. Set 'true' for direct answer attempts, 'false' for clarification requests, hint seeking, or tangential responses.",
          },
          gptResponseToStudentText: {
            type: ['string', 'null'],
            description:
              "Convert mathematical feedback into natural, spoken language. Transform KaTeX notation into clear verbal descriptions (e.g., '$\\\\frac{1}{2}$' becomes 'one half'). Maintain supportive tone while ensuring mathematical clarity.",
          },
          isHintRequest: {
            type: 'boolean',
            description: `Set 'true' if the student's question requests problem-solving help, hints, or guidance specific to answering the question. Set 'false' for general course/procedure questions, clarification requests, or non-content inquiries.`,
          },
          isLastQuestionCompleted: {
            type: 'boolean',
            description: `Keep 'true' if the last question in conversation was completed, and new one or similar not provided yet. This also includes cases where: - The tutor confirms that the student's answer for the last question is correct. - The student answered the last question correctly. - The tutor reveals the correct answer, the correct answer with solution, or the correct answer with reasoning for the last question. Set 'false' immediately when a new question is provided. This includes cases where: - The correct answer was not provided by student for the last question in conversation. - The student requests for a new question or similar question. - You generate a new or similar question for the student. - You generate a new follow-up question for the student.`,
          },
          isSolutionResponse: {
            type: 'boolean',
            description: `Set to true if this response reveals step by step solution requested with "Request for solution pattern".`,
          },
          ...(isSolutionRequest
            ? {
                tip: {
                  type: ['string', 'null'],
                  description: `Provide a helpful strategy or shortcut for similar problems. Use when: 1) There's a reusable problem-solving approach, 2) A particular technique simplifies solutions, 3) It helps generalize the learning. Format: "For similar problems, try [concrete action] because [brief rationale]." Only include when directly relevant to the solution and isSolutionResponse is set to "true".`,
                },
                commonMistake: {
                  type: ['string', 'null'],
                  description: `Describe typical errors students make with this problem. Use when: 1) There's a common misconception, 2) The error pattern recurs frequently, 3) It helps prevent future mistakes. Format: "Students often [specific error] because [reason]. Remember that [correction]." Include only for solution responses when isSolutionResponse is set to "true".`,
                },
              }
            : {}),
          ...(shouldProcessMarks
            ? {
                actualMarks: {
                  type: 'number',
                  description: `For interactive papers only: The actual number of marks awarded to the student based on their current answer. This must be an integer between 0 and ${totalMarks}. DO NOT use decimal or fractional marks - round to the nearest whole number. Carefully evaluate the answer against the mark scheme criteria, using standard exam notation for method marks (M1, M2) and accuracy marks (A1, A2). Award method marks (M) for correct approaches and working shown, even if the final answer is incorrect. Award accuracy marks (A) for correct numerical results or conclusions. For each mark awarded, document exactly what the student did to earn that mark. The total marks should be the sum of all M and A marks. The detailed explanation for each awarded mark must be provided in the 'marksBreakdown' field.`,
                },
                marksBreakdown: {
                  type: 'string',
                  description:
                    'A bulleted list of each specific mark awarded based on the mark scheme. Separate each mark with descriptions using the format "M1: [description of method mark]" or "A1: [description of accuracy mark]". Start each mark description from a new line. This breakdown helps the student understand exactly which parts of their answer earned marks. This field should contain only the list of marks, without any introductory text like "Marks awarded:". **If actualMarks is 0, return an empty string ("").**',
                },
                totalMarks: {
                  type: 'number',
                  description: `For interactive papers only: The total number of marks available for this question is ${totalMarks}. Return this exact value as a number, not a string.`,
                },
                lineAnnotations: {
                  type: 'array',
                  description:
                    'For interactive papers only: walk the student\'s working line by line and tag EACH step with how it fared against the mark scheme. One entry per distinct step or line of the student answer (quote it briefly). status: "earned" when the step earned its mark-scheme code (green in the UI), "slipped" when this step is where a mark was lost (amber in the UI), "info" for neutral context. markCode: the code from the mark scheme this step maps to (e.g. M1, A1) or "none". note: one short sentence - what the mark was earned for, or exactly what went wrong and what the correct step was. Keep every note under 20 words.',
                  items: {
                    type: 'object',
                    additionalProperties: false,
                    properties: {
                      line: {
                        type: 'string',
                        description:
                          "The student's step, quoted briefly (LaTeX allowed).",
                      },
                      markCode: {
                        type: 'string',
                        description:
                          'Mark scheme code this step maps to (M1, A1, ...) or "none".',
                      },
                      status: {
                        type: 'string',
                        enum: ['earned', 'slipped', 'info'],
                        description:
                          'earned = mark gained here, slipped = mark lost here, info = neutral.',
                      },
                      note: {
                        type: 'string',
                        description:
                          'One short sentence: why earned, or what went wrong + the fix.',
                      },
                    },
                    required: ['line', 'markCode', 'status', 'note'],
                  },
                },
              }
            : {}),
        },
        required: [
          'gptResponseToStudent',
          'points',
          'isCompleted',
          'isAnswerRelevant',
          'answerRelevantDescription',
          'isAnswered',
          'studentAnswerIncludesKaTexSyntax',
          'isPartiallyCorrect',
          'incrementGivenHintCount',
          'incrementAttemptCount',
          'gptResponseToStudentText',
          'isHintRequest',
          'isLastQuestionCompleted',
          'isSolutionResponse',
          ...(isSolutionRequest ? ['tip', 'commonMistake'] : []),
          ...(shouldProcessMarks
            ? [
                ...SCHEME_CHECK_REQUIRED,
                'actualMarks',
                'totalMarks',
                'marksBreakdown',
                'lineAnnotations',
              ]
            : []),
        ],
      },
    },
  }
}

export function getDynamicPaperJsonSchema(config: {
  totalMarks?: number
  attemptCount?: number
  givenHintCount?: number
  currentQuestion?: string
  processMarks?: boolean
  isSolutionRequest?: boolean
}): ResponseFormatJSONSchema {
  const {
    totalMarks = 0,
    attemptCount = 0,
    givenHintCount = 0,
    currentQuestion = '',
    processMarks = false,
    isSolutionRequest = false,
  } = config
  const shouldProcessMarks = processMarks && totalMarks > 0

  return generateLessonWithMarksAvailableJsonSchema({
    shouldProcessMarks,
    totalMarks,
    attemptCount,
    givenHintCount,
    currentQuestion,
    isSolutionRequest,
  })
}

export function generateModelSolutionJsonSchema(): ResponseFormatJSONSchema {
  return {
    type: 'json_schema',
    json_schema: {
      strict: true, // means all fields are required
      name: 'provide_model_solution',
      description: 'Generate exam-style model solution based on mark scheme',
      schema: {
        type: 'object',
        additionalProperties: false,
        properties: {
          gptResponseToStudent: {
            type: 'string',
            description:
              'Complete model solution with proper formatting that addresses all mark scheme points',
          },
        },
        required: ['gptResponseToStudent'],
      },
    },
  }
}

export function getInteractivePaperJsonSchema(config: {
  totalMarks?: number
  attemptCount?: number
  givenHintCount?: number
  currentQuestion?: string
  isInteractivePaper?: boolean
  isSolutionRequest?: boolean
  schemeFlagged?: boolean
}): ResponseFormatJSONSchema {
  const {
    totalMarks = 0,
    attemptCount = 0,
    givenHintCount = 0,
    currentQuestion = '',
    isInteractivePaper = false,
    isSolutionRequest = false,
    schemeFlagged = false,
  } = config
  const shouldProcessMarks = isInteractivePaper && totalMarks > 0

  return generateLessonWithMarksAvailableJsonSchema({
    shouldProcessMarks,
    totalMarks,
    attemptCount,
    givenHintCount,
    currentQuestion,
    isSolutionRequest,
    schemeFlagged,
  })
}
